import { ApiError, fetchRescheduleAvailability, rescheduleBooking, cancelBookingByAdmin, } from '../../../api/adminApi';
import { formatUtcTimeRange } from '../../../utils/format';
import { t, formatDurationFromMinutes, isUnauthorized, isNotFound, openConfirmModal, createModalOverlay as createOverlay, createModalButton as createButton, } from './context';
import { bookableWindows, createAvailabilityStatusRow, createSlotPicker } from '../slotPicker';
const RESCHEDULE_ERROR_KEYS = {
    slot_unavailable: 'admin.bookings.rescheduleModal.errors.slotUnavailable',
    insufficient_balance: 'admin.bookings.rescheduleModal.errors.insufficientBalance',
    subscription_expired: 'admin.bookings.rescheduleModal.errors.subscriptionExpired',
    booking_past: 'admin.bookings.rescheduleModal.errors.past',
    day_closed: 'admin.bookings.rescheduleModal.errors.dayClosed',
    already_cancelled: 'admin.bookings.rescheduleModal.errors.cancelled',
};
const CANCEL_ERROR_KEYS = {
    already_cancelled: 'admin.bookings.cancelModal.errors.cancelled',
    booking_past: 'admin.bookings.cancelModal.errors.past',
};
// "Edit booking" menu, opened from a booking's place or time cell in the
// "Bookings" calendar table. `onChanged` refreshes the calendar after an edit.
export function openEditBookingModal(ctx, booking, onChanged) {
    const { overlay, modal } = createOverlay(ctx, 'admin__modal admin__modal--confirm');
    const title = document.createElement('h2');
    title.className = 'admin__modal-title';
    title.textContent = t('admin.bookings.editModal.title');
    const actions = document.createElement('div');
    actions.className = 'admin__modal-stack';
    const rescheduleButton = createButton('cta admin__submit', t('admin.bookings.editModal.reschedule'), () => {
        overlay.remove();
        openRescheduleModal(ctx, booking, onChanged);
    });
    const cancelBookingButton = createButton('cta admin__submit', t('admin.bookings.editModal.cancelBooking'), () => {
        overlay.remove();
        openCancelBookingModal(ctx, booking, onChanged);
    });
    const closeButton = createButton('cta', t('admin.bookings.editModal.close'), () => overlay.remove());
    actions.append(rescheduleButton, cancelBookingButton, closeButton);
    modal.append(title, actions);
}
// "Cancel booking": a yes/no confirmation. "No" just closes it; "Yes"
// cancels (the server refunds a subscription booking's minutes and emails
// both sides) and re-loads the table.
function openCancelBookingModal(ctx, booking, onChanged) {
    openConfirmModal(ctx, {
        title: t('admin.bookings.cancelModal.title'),
        text: t('admin.bookings.cancelModal.question', {
            place: booking.placeName,
            time: formatUtcTimeRange(booking.dateStart, booking.dateEnd),
            customer: booking.customerName,
        }),
        noLabel: t('admin.bookings.cancelModal.no'),
        yesLabel: t('admin.bookings.cancelModal.yes'),
        yesClassName: 'cta admin__delete',
        errorText: t('admin.bookings.cancelModal.errors.generic'),
        errorTextFor: (err) => {
            const key = err instanceof ApiError && err.code ? CANCEL_ERROR_KEYS[err.code] : undefined;
            return key ? t(key) : undefined;
        },
        logMessage: 'Failed to cancel booking:',
        action: () => cancelBookingByAdmin(booking.id),
        onDone: onChanged,
    });
}
// "Change time": the booking's day is fixed, only its interval moves — the
// same window chips + start/end selects as the public booking page, fed by
// windows in which the booking's own current slot counts as free.
function openRescheduleModal(ctx, booking, onChanged) {
    const { overlay, modal } = createOverlay(ctx, 'admin__modal');
    const title = document.createElement('h2');
    title.className = 'admin__modal-title';
    title.textContent = t('admin.bookings.rescheduleModal.title');
    const meta = document.createElement('p');
    meta.className = 'admin__reschedule-meta';
    meta.textContent = `${booking.customerName} · ${booking.placeName}`;
    const body = document.createElement('div');
    body.className = 'admin__reschedule';
    const error = document.createElement('p');
    error.className = 'admin__error';
    error.hidden = true;
    const actions = document.createElement('div');
    actions.className = 'admin__modal-confirm-actions';
    const cancelButton = createButton('cta', t('admin.bookings.rescheduleModal.cancel'), () => overlay.remove());
    const saveButton = createButton('cta admin__submit', t('admin.bookings.rescheduleModal.save'), () => save());
    saveButton.disabled = true;
    actions.append(cancelButton, saveButton);
    modal.append(title, meta, body, error, actions);
    let availability = null;
    // The picker owns the current start/end — read it with picker.getSelection()
    // rather than keeping a second copy in sync via onChange.
    let picker = null;
    function currentSelection() {
        return picker?.getSelection() ?? { start: null, end: null };
    }
    function showError(message) {
        error.textContent = message;
        error.hidden = false;
    }
    function showMessage(message) {
        body.innerHTML = '';
        const text = document.createElement('p');
        text.className = 'booking__empty';
        text.textContent = message;
        body.appendChild(text);
        saveButton.disabled = true;
    }
    function load(errorAfterLoad) {
        showMessage(t('admin.bookings.rescheduleModal.loading'));
        fetchRescheduleAvailability(booking.id)
            .then((response) => {
            availability = response;
            render(response);
            if (errorAfterLoad)
                showError(errorAfterLoad);
        })
            .catch((err) => {
            if (isUnauthorized(err)) {
                overlay.remove();
                ctx.showLogin();
                return;
            }
            if (err instanceof ApiError && (isNotFound(err) || err.code === 'already_cancelled')) {
                overlay.remove();
                onChanged();
                return;
            }
            console.error('Failed to load reschedule availability:', err);
            showMessage(t('admin.bookings.rescheduleModal.loadError'));
        });
    }
    function render(response) {
        body.innerHTML = '';
        body.appendChild(createAvailabilityStatusRow(response.date, response.stepMinutes));
        const windows = bookableWindows(response.windows);
        if (response.ended || response.closed || windows.length === 0) {
            picker = null;
            const message = document.createElement('p');
            message.className = 'booking__empty';
            message.textContent = response.ended
                ? t('admin.bookings.rescheduleModal.ended')
                : response.closed
                    ? t('admin.bookings.rescheduleModal.closed')
                    : t('admin.bookings.rescheduleModal.noWindows');
            body.appendChild(message);
            saveButton.disabled = true;
            return;
        }
        // Preselects the booking's current interval — it normally fits one window,
        // since its own slot counts as free. It doesn't when the day has since been
        // closed, today's elapsed part cut into it, or a neighbouring booking sits
        // closer than BOOKING_BUFFER_MINUTES (booked before the break existed) —
        // then the picker just starts with nothing selected.
        picker = createSlotPicker({
            windows,
            stepMinutes: response.stepMinutes,
            initial: { start: response.startMinutes, end: response.endMinutes },
            onChange: () => {
                error.hidden = true;
                renderBalance();
            },
        });
        const balance = document.createElement('p');
        balance.className = 'booking__summary admin__reschedule-balance';
        body.append(picker.element, balance);
        renderBalance();
    }
    // The customer's subscription balance, the over-balance warning and the
    // Save button's state all depend only on the chosen start/end.
    function renderBalance() {
        if (!availability)
            return;
        const balance = body.querySelector('.admin__reschedule-balance');
        if (!balance)
            return;
        body.querySelector('.admin__reschedule-warning')?.remove();
        const { start, end } = currentSelection();
        saveButton.disabled =
            start === null || end === null || (start === availability.startMinutes && end === availability.endMinutes);
        const leftMinutes = availability.subscriptionLeftMinutes;
        balance.hidden = leftMinutes === null;
        if (leftMinutes === null)
            return;
        balance.textContent = t('admin.bookings.rescheduleModal.balance', { time: formatDurationFromMinutes(leftMinutes) });
        // A longer interval draws the extra minutes from the same subscription;
        // warn (and block Save) when it has expired or the balance can't cover
        // them — the server rejects it with subscription_expired /
        // insufficient_balance anyway.
        if (start !== null && end !== null) {
            const extraMinutes = end - start - (availability.endMinutes - availability.startMinutes);
            const expired = availability.subscriptionExpired && extraMinutes > 0;
            if (expired || extraMinutes > leftMinutes) {
                const warning = document.createElement('p');
                warning.className = 'admin__error admin__reschedule-warning';
                warning.textContent = expired
                    ? t('admin.bookings.rescheduleModal.expiredWarning')
                    : t('admin.bookings.rescheduleModal.insufficientWarning', {
                        available: formatDurationFromMinutes(leftMinutes),
                        needed: formatDurationFromMinutes(extraMinutes - leftMinutes),
                    });
                balance.insertAdjacentElement('afterend', warning);
                saveButton.disabled = true;
            }
        }
    }
    function save() {
        const { start, end } = currentSelection();
        if (start === null || end === null || saveButton.disabled)
            return;
        error.hidden = true;
        saveButton.disabled = true;
        cancelButton.disabled = true;
        saveButton.classList.add('is-loading');
        rescheduleBooking(booking.id, start, end)
            .then(() => {
            overlay.remove();
            onChanged();
        })
            .catch((err) => {
            saveButton.classList.remove('is-loading');
            cancelButton.disabled = false;
            if (isUnauthorized(err)) {
                overlay.remove();
                ctx.showLogin();
                return;
            }
            if (isNotFound(err)) {
                overlay.remove();
                onChanged();
                return;
            }
            const code = err instanceof ApiError ? err.code : undefined;
            const errorKey = (code && RESCHEDULE_ERROR_KEYS[code]) || 'admin.bookings.rescheduleModal.errors.generic';
            if (code === 'slot_unavailable' || code === 'insufficient_balance' || code === 'subscription_expired' || code === 'booking_past') {
                // Someone else's booking or a balance change landed meanwhile —
                // re-fetch so the picker reflects the current state.
                load(t(errorKey));
                return;
            }
            console.error('Failed to reschedule booking:', err);
            showError(t(errorKey));
            saveButton.disabled = false;
        });
    }
    load();
}
